import HealingIcon from "@mui/icons-material/Healing";
import { Box, Chip, Stack } from "@mui/material";
import type {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid";
import React from "react";
import { useNavigate } from "react-router";
import { CreatedByColumn } from "../components/created-by-column";
import { DataGrid } from "../components/data-grid";
import { GitBranchChip } from "../components/git-branch-chip";
import { PageHeader } from "../components/page-header";
import { RunStatusChip } from "../components/run-status-chip";
import { RunsFilters } from "../components/runs-filters";
import { useDebounce } from "../hooks/use-debounce";
import { usePaginatedApiQuery } from "../hooks/use-paginated-api-query";
import { useUrlFilters } from "../hooks/use-url-filters";
import { type TestRun, TestRunStatusList } from "../types";
import { formatDurationFromTimes } from "../utils";

const columns: GridColDef<TestRun>[] = [
  {
    field: "name",
    headerName: "Name",
    minWidth: 240,
    flex: 1,
    type: "string",
    sortable: true,
  },
  {
    field: "run_status",
    type: "singleSelect",
    valueOptions: TestRunStatusList,
    headerName: "Status",
    renderCell: (params: GridRenderCellParams<TestRun>) => {
      return <RunStatusChip status={params.row.final_run_status ?? params.row.run_status} />;
    },
    width: 180,
    sortable: true,
  },
  {
    field: "start_time",
    type: "dateTime",
    headerName: "Started At",
    valueGetter: (val: TestRun["start_time"]) => {
      if (!val) {
        return null;
      }

      return new Date(val);
    },
    width: 200,
    sortable: true,
  },
  {
    field: "elapsed",
    type: "string",
    headerName: "Duration",
    valueGetter: (_, val) => {
      return formatDurationFromTimes(val?.start_time, val?.end_time);
    },
    width: 120,
    sortable: false,
  },
  {
    field: "rerun",
    type: "boolean",
    headerName: "Re-run",
    valueGetter: (_, val) => {
      return !!val?.rerun_id;
    },
    width: 80,
    sortable: false,
  },
  {
    field: "matrix",
    headerName: "Variant",
    width: 220,
    sortable: false,
    valueGetter: (_, val) => {
      if (!val?.matrix_option_id) return null;
      // Server stamps "Test run for <name> - (k=v, ...)" — pull the suffix.
      const m = /\s-\s(\(.+\))$/.exec(val.name);
      if (m) return m[1];
      // Defensive fallback: regex miss → truncate matrix_option_id to 8 chars.
      return val.matrix_option_id.slice(0, 8);
    },
    renderCell: (params: GridRenderCellParams<TestRun>) => {
      if (!params.row.matrix_option_id) {
        return null;
      }
      return <Chip size="small" label={params.value} variant="outlined" />;
    },
  },
  {
    field: "allow_fix",
    type: "boolean",
    headerName: "Auto-Fix",
    valueGetter: (_, val) => {
      return !!val?.allow_fix;
    },
    width: 100,
    renderCell: (params: GridRenderCellParams<TestRun>) => {
      if (params.row.allow_fix) {
        return <HealingIcon color="inherit" fontSize="inherit" sx={{ alignSelf: "center" }} />;
      }
      return null;
    },
  },
  {
    field: "created_by",
    type: "string",
    headerName: "Started By",
    width: 150,
    renderCell: CreatedByColumn,
    sortable: false,
  },
  {
    field: "git_branch",
    type: "string",
    headerName: "Branch",
    width: 150,
    sortable: false,
    renderCell: (params: GridRenderCellParams<TestRun>) => {
      return (
        <GitBranchChip
          branch={params.row.git_branch}
          commitSha={params.row.git_commit_sha}
          commitShaShort={params.row.git_commit_sha_short}
          commitMessage={params.row.git_commit_message}
          remoteOrigin={params.row.git_remote_origin}
          isDirty={params.row.git_is_dirty}
          showCommit
        />
      );
    },
  },
];

type RunsPageProps = {
  rerunId?: string;
  testId?: string;
  suiteId?: string;
  suiteRunId?: string;
  noHeader?: boolean;
};

export default function TestRunsPage(props: RunsPageProps) {
  // URL-based filter state (persists across navigation)
  const [filters, setFilters] = useUrlFilters({
    search: "",
    status: "",
    branch: "",
    ciRunId: "",
  });
  const nav = useNavigate();

  const q = new URLSearchParams();

  if (props.suiteId) {
    q.append("suite_id", props.suiteId);
  }

  if (props.suiteRunId) {
    q.append("suite_run_id", props.suiteRunId);
  }

  if (props.testId) {
    q.append("test_id", props.testId);
  }

  if (props.rerunId) {
    q.append("rerun_id", props.rerunId);
  }

  const baseQuery = `/runs${q.toString() ? `?${q.toString()}` : ""}`;

  const {
    data,
    isLoading,
    paginationState,
    setPaginationState,
    sortModel,
    setSortModel,
    setFilters: setApiFilters,
  } = usePaginatedApiQuery<TestRun>(baseQuery);

  const debouncedSearchQuery = useDebounce(filters.search, 300);
  const debouncedCiRunId = useDebounce(filters.ciRunId, 300);

  // Update API filters when search query changes
  React.useEffect(() => {
    if (debouncedSearchQuery !== undefined) {
      setPaginationState((prev) => ({ ...prev, page: 0 }));
      setApiFilters((prev) => ({
        ...prev,
        q: debouncedSearchQuery,
      }));
    }
  }, [debouncedSearchQuery, setPaginationState, setApiFilters]);

  // Update API filters when status filter changes
  React.useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setApiFilters((prev) => ({
      ...prev,
      run_status: filters.status || undefined,
    }));
  }, [filters.status, setPaginationState, setApiFilters]);

  // Update API filters when branch filter changes
  React.useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 0 }));
    setApiFilters((prev) => ({
      ...prev,
      git_branch: filters.branch || undefined,
    }));
  }, [filters.branch, setPaginationState, setApiFilters]);

  // Update API filters when CI run ID filter changes
  React.useEffect(() => {
    if (debouncedCiRunId !== undefined) {
      setPaginationState((prev) => ({ ...prev, page: 0 }));
      setApiFilters((prev) => ({
        ...prev,
        ci_run_id: debouncedCiRunId || undefined,
      }));
    }
  }, [debouncedCiRunId, setPaginationState, setApiFilters]);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationState({
      page: model.page,
      pageSize: model.pageSize,
    });
  };

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handleRowClick = (params: GridRowParams<TestRun>) => {
    nav(`/runs/${params.row.id}`);
  };

  return (
    <Box
      data-testid="runs-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "85vh",
        overflow: "hidden",
      }}
    >
      {!props?.noHeader && (
        <PageHeader title="Test Runs" subtitle="View and analyze your test execution history">
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <RunsFilters
                searchQuery={filters.search}
                statusFilter={filters.status}
                branchFilter={filters.branch}
                ciRunIdFilter={filters.ciRunId}
                onSearchChange={(search) => setFilters({ search })}
                onStatusChange={(status) => setFilters({ status })}
                onBranchChange={(branch) => setFilters({ branch })}
                onCiRunIdChange={(ciRunId) => setFilters({ ciRunId })}
                searchPlaceholder="Search by test name and status..."
              />
            </Box>
          </Stack>
        </PageHeader>
      )}

      <DataGrid
        loading={isLoading}
        rows={data ?? []}
        columns={columns}
        pagination
        paginationMode="server"
        paginationModel={paginationState}
        onPaginationModelChange={handlePaginationModelChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        rowCount={-1} // Unknown row count for server-side pagination
        containerSx={{
          height: "100%",
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
        }}
        onRowClick={handleRowClick}
      />
    </Box>
  );
}
